import { EXAM_EVENT_DATA_TEST_ID } from '@e2e/constants/exam-center';
import {
  Grid,
  Text,
  FormSearchMultipleSelection,
  type ItemComponentProps,
  Checkbox,
} from '@shared/components';
import { useMemo } from 'react';

export interface ExamCoursesFormFieldProps {
  slotProps?: {
    examCourses?: {
      disabled?: boolean;
      isLoading: boolean;
      isExamCourseSelected?: boolean;
      examCoursesOptions?: {
        label: string;
        value: string;
      }[];
      examCourseSelected: {
        label: string;
        value: string;
      }[];
      setSelected: (value: { label: string; value: string }[]) => void;
      multiple?: boolean;
    };
  };
}

interface ExamEventExamCourseSelectionItemProps extends ItemComponentProps {
  isAllSelected?: boolean;
  isSomeSelected?: boolean;
}

function ExamEventExamCourseSelectionItem(props: Readonly<ExamEventExamCourseSelectionItemProps>) {
  const { option, isAllSelected, isSomeSelected } = props;
  return (
    <>
      <Checkbox
        data-testid={option.label}
        indeterminate={option.value === 'all' ? isSomeSelected : false}
        checked={isAllSelected ? true : props.selected}
      />
      <Text>{option.label}</Text>
    </>
  );
}

export default function ExamCoursesFormField(props: Readonly<ExamCoursesFormFieldProps>) {
  const { slotProps } = props;
  const { multiple: isMultiple = false, disabled: isExamCoursesDisabled = false } =
    slotProps?.examCourses ?? {};

  const options = slotProps?.examCourses?.examCoursesOptions ?? [];
  const selected = slotProps?.examCourses?.examCourseSelected ?? [];
  const isAllSelected = selected.some((s) => s.value === 'all');

  const selectAllOption = useMemo(() => {
    return options.find((option) => option.value === 'all');
  }, [options]);

  const isSomeSelected = selected.length > 0 && !isAllSelected;

  return (
    <Grid
      sx={{
        gridTemplateColumns: '240px 1fr',
        gap: (theme) => theme.spacing(6),
      }}
    >
      <Text variant='body1Semibold'>หลักสูตรทดสอบ</Text>
      <FormSearchMultipleSelection
        name='examCourses'
        itemComponent={(p: ItemComponentProps) => (
          <ExamEventExamCourseSelectionItem
            {...p}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
          />
        )}
        options={options}
        value={selected}
        label='หลักสูตรที่เปิดสอบ'
        data-testid={EXAM_EVENT_DATA_TEST_ID.EXAM_COURSE_DROPDOWN}
        disabled={isExamCoursesDisabled}
        loading={slotProps?.examCourses?.isLoading}
        onChange={(_, newValues, reason, details) => {
          const clicked = details?.option as { label: string; value: string } | undefined;

          if (!isMultiple) {
            // setSelected only latest select option when multiple selection is not allow
            const selectedOptions = newValues.slice(-1);

            return slotProps?.examCourses?.setSelected(
              selectedOptions.map((selectedOption) => ({
                label: selectedOption.label,
                value: selectedOption.value as string,
              })),
            );
          }

          if (clicked?.value === 'all') {
            if (reason === 'selectOption') {
              slotProps?.examCourses?.setSelected(selectAllOption ? [selectAllOption] : []);
            } else {
              slotProps?.examCourses?.setSelected([]);
            }
            return;
          }

          if (isAllSelected && clicked) {
            const examCourseIdsWithoutClicked = options.filter(
              (o) => o.value !== clicked.value && o.value !== 'all',
            );

            slotProps?.examCourses?.setSelected(examCourseIdsWithoutClicked);
            return;
          }

          const selectedValues = newValues.map((o) => ({
            label: o.label,
            value: String(o.value),
          }));
          // If all options are selected, set 'all' option
          if (selectedValues.length === options.length) {
            slotProps?.examCourses?.setSelected(selectAllOption ? [selectAllOption] : []);
            return;
          }
          slotProps?.examCourses?.setSelected(selectedValues);
        }}
        placeholder={selected.length > 0 ? '' : 'เลือกหลักสูตรที่เปิดสอบ'}
      />
    </Grid>
  );
}
